import {padStart} from "luxon/src/impl/util";
import {Controller} from "stimulus";
import {setCookie, deleteCookie, getCookie} from "init/cookie";
import MicroModal from "micromodal";
import {DateTime} from "luxon";
import cart from "util/cart.js";
import newCart, {book_now, compare_item_and_booking_item} from "util/new_cart.js";
import unitQuote from "util/quote.js";
import {useDispatch} from "stimulus-use";
import order from "util/order";
import {errorMessageToReplace, errorMessageForReplacing} from './constants';
import {_moneyFormat} from "util/formatters";
import getFilters from "../../util/filter_params";


export default class extends Controller {
  static targets = [
    "dropdown",
    "dropdownBtn",
    "datesGuestsPicker",
    "adultsCount",
    "childrenCount",
    "arriveInput",
    "departInput",
    "datesLabelMobile",
    "datesLabel",
    "guestsCountMobile",
    "collapse",
    "rateInput",
    "rateName",
    "ratePrice",
    "bookingBtn",
    "priceBlock",
    "initialSidebarContent",
    "sidebarContent",
    "checkAvalabilityBtn",
    "arriveDateLabel",
    "departDateLabel",
    "guestsCount",
    "unitId",
    "errorsContainer",
    "feeItems",
    "feesTotal",
    "nightlyRate",
    "totalRate",
    "totalPrice",
    "pricePerGuest",
    "bottomBarTitle",
    "arriveDropdownBtn",
    "departDropdownBtn",
    "unitType",
    "addToCartBtn",
    "addedToCartBtn",
    "updateInCartBtn",
    "bookingBtnForm",
    "unitTypeId",
    "cartItemsCount",
    "rateNight",
    "tooltipText",
    "guestsWrapper",
    "totalPriceBlock",
    "pickOwnVillaBtn",
    "priceRateTypeTemplate",
    "priceRateTypes",
    "priceRateTypeInput",
    "couponDiscount",
    "couponCode",
    "couponDiscountValue",
    "couponSavingsBanner",
    "couponSavingsCode",
    "couponSavingsValue",
    "promoRateHeader",
    "promoRateOldPrice",
    "promoRateNewPrice"
  ];

  currentQuote = undefined;

  connect() {
    useDispatch(this);
    const subHeader = document.querySelector(".sub-header-container");
    MicroModal.init({
      disableScroll: true,
      onShow: () => this.updateHeaderVisibility(subHeader),
      onClose: () => this.updateHeaderVisibility(subHeader)
    });
    // this.errorQuoteResponse = false;
    this.updateDates();
    this.updateGuestsCount();
    this.onlyOneCalendarDatePicked = false;

    if (this.isValidDates() && this.isValidDGuests()) {
      this.updateUrlAndSendQuoteRequest();
    } else {
      this.showAddToCartButton();
      this.disableAddToCartButton();
    }

    this.saveSearchDataToCookies();
    this.errorQuoteResponse = false;
    this.updateCartItemsCount();
  }

  handleItemQuoteResponse(item, response) {
    if (response.status === "error") {
      newCart.remove_item(item.params.id)
    }
  }

  handleMouseDown(e) {
    const isDropdown = this.dropdownTargets.find(dropdownTarget =>
      dropdownTarget.contains(e.target)
    );
    const isDropdownBtn = this.dropdownBtnTargets.find(dropdownBtnTarget =>
      dropdownBtnTarget.contains(e.target)
    );
    if (!isDropdown && !isDropdownBtn) {
      this.closeDropdowns();
    }
  }

  handleDatePickerChange() {
    this.onlyOneCalendarDatePicked = true;
    this.setActiveDatesDropdownBtn();
  }

  handleDatePickerClose() {
    this.closeDropdowns();
    this.handleDatesChanged();
    this.onlyOneCalendarDatePicked = false;
  }

  toggleDropdown(e) {
    const dropdownButton = e.currentTarget;
    const dropdown = this.dropdown(dropdownButton);

    this.dropdownTargets.forEach(dropdownTarget => {
      if (dropdownTarget === dropdown) return;
      this.closeDropdown(dropdownTarget);
    });
    dropdown.classList.toggle("open");
    if (dropdown.classList.contains("open")) {
      this.setActiveDropdownButton(dropdownButton);
    } else {
      this.resetActiveDropdowButtons();
    }
    this.updateGuestDatesPickerStyling();
  }

  closeDropdown(dropdown) {
    const dropdownBtns = this.dropdownButtons(dropdown);
    dropdown.classList.remove("open");
    dropdownBtns.forEach(dropdownBtn =>
      dropdownBtn.classList.remove("not-active", "active")
    );
  }

  resetActiveDropdowButtons() {
    this.dropdownBtnTargets.forEach(dropdownBtn =>
      dropdownBtn.classList.remove("active", "not-active")
    );
  }

  updateCartItemsCount() {
    newCart.size.then(size => {
      if (size > 0) {
        this.cartItemsCountTargets.forEach(item => {
          item.classList.remove("hidden");
          item.textContent = size;
        });
      } else {
        this.cartItemsCountTargets.forEach(item => {
          item.classList.add("hidden");
          item.textContent = "";
        });
      }
    });
  }

  dropdown(sibling) {
    const siblings = [...sibling.parentNode.children].filter(
      c => c !== sibling
    );
    const dropdown = this.dropdownTargets.find(dropdownTarget =>
      siblings.includes(dropdownTarget)
    );
    return dropdown;
  }

  dropdownButtons(sibling) {
    const siblings = [...sibling.parentNode.children].filter(
      c => c !== sibling
    );
    const dropdownButtons = this.dropdownBtnTargets.filter(dropdownBtnTarget =>
      siblings.includes(dropdownBtnTarget)
    );
    return dropdownButtons;
  }

  closeDropdowns() {
    this.dropdownTargets.forEach(dropdownTarget => {
      this.closeDropdown(dropdownTarget);
    });
    this.updateGuestDatesPickerStyling();
  }

  setActiveDropdownButton(activeDropdownBtn) {
    this.dropdownBtnTargets.forEach(dropdownBtn => {
      dropdownBtn.classList.remove("active");
      dropdownBtn.classList.add("not-active");
    });

    if (
      activeDropdownBtn === this.arriveDropdownBtnTarget ||
      activeDropdownBtn === this.departDropdownBtnTarget
    ) {
      this.setActiveDatesDropdownBtn();
    } else {
      activeDropdownBtn.classList.remove("not-active");
      activeDropdownBtn.classList.add("active");
    }
  }

  setActiveDatesDropdownBtn() {
    if (this.onlyOneCalendarDatePicked) {
      this.arriveDropdownBtnTarget.classList.remove("active");
      this.arriveDropdownBtnTarget.classList.add("not-active");
      this.departDropdownBtnTarget.classList.remove("not-active");
      this.departDropdownBtnTarget.classList.add("active");
    } else {
      this.departDropdownBtnTarget.classList.remove("active");
      this.departDropdownBtnTarget.classList.add("not-active");
      this.arriveDropdownBtnTarget.classList.remove("not-active");
      this.arriveDropdownBtnTarget.classList.add("active");
    }
  }

  updateDates() {
    const filters = getFilters({
      checkin: this.arriveInputTarget.value,
      checkout: this.departInputTarget.value
    }, false);

    if (!filters.has('checkin') || !filters.has('checkout')) {
      return;
    }

    // replace '-' with '/' to avoid effecting timezone when creating date object from a string
    const arriveDate = DateTime.fromJSDate(
      new Date(filters.get('checkin').replace(/-/g, "/"))
    );
    const departDate = DateTime.fromJSDate(
      new Date(filters.get('checkout').replace(/-/g, "/"))
    );
    const arriveDateFull = arriveDate.toFormat("MMM d, yyyy");
    const departDateFull = departDate.toFormat("MMM d, yyyy");
    const arriveDateShort = arriveDate.toFormat("MM/dd/yy");
    const departDateShort = departDate.toFormat("MM/dd/yy");
    this.arriveDateLabelTarget.textContent = arriveDateFull;
    this.departDateLabelTarget.textContent = departDateFull;
    this.datesLabelMobileTarget.textContent = `${arriveDateShort} - ${departDateShort}`;
    this.updateSubmitButtonState();
    this.updateCartButtonState();
  }

  updateGuestsCount() {
    const filters = getFilters({
      adults: this.adultsCountTarget.value,
      children: this.childrenCountTarget.value
    }, false);

    this.adultsCountTarget.value = filters.get('adults') || 2;
    this.childrenCountTarget.value = filters.get('children') || 0;

    const adultsCount = Number(filters.get('adults') || 2);
    const childrenCount = Number(filters.get('children') || 0);
    const adultsWording = adultsCount === 1 ? "Adult" : "Adults";
    const childrenWording = childrenCount === 1 ? "Child" : "Children";
    const guestsWording =
      adultsCount + childrenCount === 1 ? "Guest" : "Guests";
    let guestsCountText = "Select Guests";

    if (adultsCount || childrenCount) {
      guestsCountText = `
        ${adultsCount +
      childrenCount} ${guestsWording} (${adultsCount} ${adultsWording}, ${childrenCount} ${childrenWording})
         `;
    }
    this.guestsCountTarget.textContent = guestsCountText;
    this.guestsCountMobileTarget.textContent = `${adultsCount +
    childrenCount} ${guestsWording}`;
    this.updateSubmitButtonState();
    this.updateCartButtonState();
  }

  updateGuestDatesPickerStyling() {
    const openedDropdown = this.dropdownTargets.find(dropdownTarget =>
      dropdownTarget.classList.contains("open")
    );
    if (openedDropdown) {
      this.datesGuestsPickerTarget.classList.add("sharp-corners");
    } else {
      this.datesGuestsPickerTarget.classList.remove("sharp-corners");
    }
  }

  resetDates() {
    this.datesLabelMobileTarget.textContent = `Select Dates`;
    this.arriveDateLabelTarget.textContent = `Check in`;
    this.departDateLabelTarget.textContent = `Check out`;
    window.location.reload();
  }

  toggleCollapse(e) {
    const collapse = this.collapseTargets.find(collapseTarget =>
      collapseTarget.contains(e.currentTarget)
    );
    collapse.classList.toggle("expanded");
  }

  updateSubmitButtonState() {

    if (this.errorQuoteResponse) {
      return;
    }

    if (!this.isValidDates()) {
      this.bookingBtnTarget.value = "Choose Dates";
      this.disableProceedToBookingButton();
      return;
    }

    if (!this.isValidDGuests()) {
      this.bookingBtnTarget.value = "Enter # of Guests";
      this.disableProceedToBookingButton();
      return;
    } else {
      this.guestsWrapperTarget.classList.remove("restricted-guests");
      this.enableProceedToBookingButton();
      this.enableAddToCartButton();
      this.enablePriceBlocks();
    }
  }

  isValidDates() {
    return !!(this.arriveInputTarget.value && this.departInputTarget.value);
  }

  isValidDGuests() {
    return !!Number(this.adultsCountTarget.value);
  }

  disableProceedToBookingButton() {
    this.bookingBtnTarget.classList.add("disabled-button");
  }

  addDisableAttributeProceedToBookingButton() {
    this.bookingBtnTarget.setAttribute("disabled", "");
  }

  removeDisableAttributeProceedToBookingButton() {
    this.bookingBtnTarget.removeAttribute("disabled");
  }

  disablePriceBlocks() {
    this.totalPriceBlockTarget.classList.add("disabled");
    this.priceBlockTarget.classList.add("disabled");
  }

  enablePriceBlocks() {
    this.totalPriceBlockTarget.classList.remove("disabled");
    this.priceBlockTarget.classList.remove("disabled");
  }

  enableProceedToBookingButton() {
    this.bookingBtnTarget.classList.remove("disabled-button");
    this.bookingBtnTarget.value = "Book Now";
  }

  enableAddToCartButton() {
    this.addToCartBtnTarget.removeAttribute("disabled", "");
    this.updateInCartBtnTarget.removeAttribute("disabled", "");
    this.addedToCartBtnTarget.removeAttribute("disabled", "");
  }

  disableAddToCartButton() {
    this.addToCartBtnTarget.setAttribute("disabled", "");
    this.updateInCartBtnTarget.setAttribute("disabled", "");
    this.addedToCartBtnTarget.setAttribute("disabled", "");
  }

  showAddToCartButton() {
    this.addedToCartBtnTarget.classList.add("hidden");
    this.updateInCartBtnTarget.classList.add("hidden");
    this.addToCartBtnTarget.classList.remove("hidden");
  }

  showAddedToCartButton() {
    this.addToCartBtnTarget.classList.add("hidden");
    this.updateInCartBtnTarget.classList.add("hidden");
    this.addedToCartBtnTarget.classList.remove("hidden");
  }

  showUpdateInCartButton() {
    this.addToCartBtnTarget.classList.add("hidden");
    this.addedToCartBtnTarget.classList.add("hidden");
    this.updateInCartBtnTarget.classList.remove("hidden");
  }

  async updateCartButtonState() {
    if (
      this.errorQuoteResponse ||
      !this.isValidDates() ||
      !this.isValidDGuests()
    ) {
      this.disableAddToCartButton();
      return;
    }

    const bookingItemFromCart = await newCart.get_item_by_unit(
      this.unitTypeTarget.value,
      parseInt(this.unitIdTarget.value) || null
    );

    this.enableAddToCartButton();
    this.updateCartItemsCount();

    if (bookingItemFromCart) {
      if (compare_item_and_booking_item(
        bookingItemFromCart,
        this.bookingItemParams())) {
        this.showAddedToCartButton();
      } else {
        this.showUpdateInCartButton();
      }
    } else {
      this.showAddToCartButton();
    }
  }

  urlQueryParams() {
    const adultsCount = parseInt(this.adultsCountTarget.value, 10);
    const childrenCount = parseInt(this.childrenCountTarget.value, 10);
    const checkIn = this.arriveInputTarget.value;
    const checkOut = this.departInputTarget.value;
    const coupon = this.activeCoupon();

    return {
      ...(checkIn && {checkin: checkIn}),
      ...(checkOut && {checkout: checkOut}),
      ...(adultsCount !== 0 && {adults: adultsCount}),
      ...(childrenCount !== 0 && {children: childrenCount}),
      ...(coupon && {coupon})
    };
  }

  activeCoupon() {
    const sp = new URLSearchParams(window.location.search);
    return sp.get("coupon") || getCookie("promo_code") || null;
  }

  bookingItemParams() {
    const bookingItemParams = {
      unit_id: this.unitIdTarget.value,
      type: this.unitTypeTarget.value,
      arrival_date: this.arriveInputTarget.value,
      departure_date: this.departInputTarget.value,
      adults: this.adultsCountTarget.value,
      children: this.childrenCountTarget.value,
    };

    if (this.hasPriceRateTypeInputTarget) {
      const checkedInput = this.priceRateTypeInputTargets.find(input => input.checked)
      if (!checkedInput) return (
        bookingItemParams
      )

      bookingItemParams['rate_type_id'] = checkedInput.value
    }

    const coupon = this.activeCoupon();
    if (coupon) bookingItemParams.promo_code = coupon;

    return bookingItemParams
  }

  updateUrlAndSendQuoteRequest() {
    const prevUrl = window.location.href;
    const queryParams = this.urlQueryParams();
    const query = Site.objectToQuery(queryParams);
    const url = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${query}`;

    // stop from repeat requests to Track API
    if (!Site.quoteStored || prevUrl !== url) {
      /* eslint-disable-next-line no-restricted-globals */
      if (history.pushState) {
        window.history.pushState({path: url}, "", url);
      } else {
        window.location.href = url;
      }
      this.sendQuoteRequest();
    }
  }

  handleDatesChanged() {
    this.updateUrlAndSendQuoteRequest();
    this.saveDatesToCookies();
  }

  handleGuestsChanged() {
    const queryParams = this.urlQueryParams();
    const query = Site.objectToQuery(queryParams);
    let url;
    if (query) {
      url = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${query}`;
    } else {
      url = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
    }

    /* eslint-disable-next-line no-restricted-globals */
    if (history.pushState) {
      window.history.pushState({path: url}, "", url);
    } else {
      window.location.href = url;
    }
    this.updateGuestsCount();
    // this.updatePricePerGuest();
    // this.updatePricePerNight();
    this.updatePricePerRoom();
    this.saveGuestsToCookies();
  }

  saveDatesToCookies() {
    const arrive = this.arriveInputTarget.value;
    const depart = this.departInputTarget.value;

    if (!arrive || !depart) return;

    const dates = {
      checkin: arrive,
      checkout: depart
    };
    setCookie("filter_by_dates", JSON.stringify(dates), 3);
  }

  saveGuestsToCookies() {
    const adultsCount = this.adultsCountTarget.value;
    const childrenCount = this.childrenCountTarget.value;

    if (!adultsCount && !childrenCount) return;

    const guests = {
      adults: adultsCount,
      children: childrenCount
    };
    setCookie("filter_by_guests", JSON.stringify(guests), 3);
  }

  saveSearchDataToCookies() {
    this.saveDatesToCookies();
    this.saveGuestsToCookies();
  }

  clearErrorMessage() {
    this.errorsContainerTarget.innerText = "";
    this.errorsContainerTarget.classList.add("hidden");
  }

  setErrorMessage(message) {
    this.errorsContainerTarget.innerText = message === errorMessageToReplace
      ? errorMessageForReplacing
      : message;
    this.errorsContainerTarget.classList.remove("hidden");
  }

  async sendQuoteRequest() {
    const result = await unitQuote(this.bookingItemParams());
    this.handleQuoteResponse(result);
  }

  handleQuoteResponse(response) {
    this.clearErrorMessage();
    if (response.status && response.status === "error") {
      this.setErrorMessage(response.message);
      this.disablePriceBlocks();
      this.disableProceedToBookingButton();
      this.addDisableAttributeProceedToBookingButton();
      this.disableAddToCartButton();
      this.errorQuoteResponse = true;
    } else {
      this.errorQuoteResponse = false;
      this.currentQuote = response;
      this.updateQuote(response);
      this.removeDisableAttributeProceedToBookingButton();
      this.updateSubmitButtonState();
      this.pushDataLayer(response);
      // this.pushCartDataLayer('view_item');
    }
  }

  showRateAndSetName(name) {
    if (!this.hasRateNameTarget) return;

    this.rateNameTargets.forEach(target => {
      target.textContent = name;
    });
  }

  updateRateTypes(rateTypes, currentRateTypeId, promoApplied = false) {
    this.priceRateTypesTarget.textContent = "";
    const self = this;

    if (rateTypes.length === 0) {
      this.priceRateTypesTarget.classList.add("hidden");
      return
    }


    rateTypes.forEach(rateType => {
      const template = this.priceRateTypeTemplateTarget.content.cloneNode(true);
      // const TRACK_FRIENDLY_RATE_NAME = {1: {name: 'Standard Rate', desc: 'Policies vary by unit and travel dates. The applicable terms will be provided on the checkout page prior to booking.'},
      //   51: {name: 'Standard Rate', desc: 'Policies vary by unit and travel dates. The applicable terms will be provided on the checkout page prior to booking.'},
      //   50: {name:'Advance Purchase', desc: 'This advanced purchase rate offers up to 20% off your stay. Full prepayment is required at the time of booking. This rate is non-refundable and cannot be modified or canceled. Not combinable with other promotions or discounts.'}
      // };
      template.querySelector('[data-slot="input"]').value = rateType.id;
      const isStandard = (rateType.detail.name || "").toLowerCase().includes("standard");
      template.querySelector('[data-slot="name"]').textContent =
        promoApplied && isStandard ? "Promo Rate" : rateType.detail.name;
      if (rateTypes.length === 1) {
        const input = template.querySelector('[data-slot="input"]');
        if (input) input.style.display = "none";
      }
      template.querySelector('[data-slot="desc"]').textContent = rateType.detail.desc;
      template.querySelector('[data-slot="value"]').textContent = `${_moneyFormat(rateType.nightly)}`;

      if (currentRateTypeId === rateType.id) {
        template.querySelector('[data-slot="input"]').checked = true;
      }

      // if(rateType.id === 50) {
      //   const leadDays = document
      //                .getElementById('cicle-lead-time')
      //                .dataset.days;
      //   const diffDays = Math.round(
      //     (new Date(self.currentQuote.arrival_date) - new Date()) / 86400000
      //   );

      //   if(diffDays > parseInt(leadDays)) {
      //    this.priceRateTypesTarget.appendChild(template);
      //   }

      // } else {
      //   this.priceRateTypesTarget.appendChild(template);
      // }
      //
      this.priceRateTypesTarget.appendChild(template);

    })

    this.priceRateTypesTarget.classList.remove("hidden");
  }

  updateFees(feeItems) {
    this.feeItemsTarget.innerHTML = "";
    let feesSum = 0;
    feeItems.forEach(item => {
      const feePrice = parseFloat(item.value);
      const feeItemHtml = `
                <div class="fee-item">
                    <div class="fee-item-name">
                        <span>${item.name}</span>
                    </div>
                    <span>${_moneyFormat(feePrice)}</span>
                </div>
            `;
      feesSum += parseFloat(item.value);
      this.feeItemsTarget.insertAdjacentHTML("beforeend", feeItemHtml);
    });
    this.feesTotalTarget.textContent = `${_moneyFormat(feesSum)}`;
  }

  updateNightlyAndTotalRate(nightlyRate, numNights, totalRate) {
    this.nightlyRateTarget.textContent = `${_moneyFormat(nightlyRate)} x ${numNights} nights`;
    if (this.hasRateNightTarget) {
      this.rateNightTarget.textContent = `${_moneyFormat(nightlyRate)} / nights`;
    }
    this.totalRateTarget.textContent = `${_moneyFormat(totalRate)}`;

    // for unit type only
    if (this.hasRatePriceTarget) {
      this.ratePriceTargets.forEach(target => {
        target.textContent = `${_moneyFormat(nightlyRate)} / night`;
      });
    }
  }

  updateHeaderVisibility(subHeader) {
    if (subHeader instanceof Element) {
      const computedStyle = window.getComputedStyle(subHeader);
      if (computedStyle.display === "none") {
        subHeader.style.display = "";
      } else {
        subHeader.style.display = "none";
      }
    }
  }

  updatePricePerRoom() {
    const numNights = this.numNights || 0;
    const pricePerGuestPerNight = this.totalPrice / parseInt(this.element.dataset.bedrooms) / numNights;
    this.pricePerGuestTarget.textContent =   `${_moneyFormat(pricePerGuestPerNight)} / night`;
  }

  updatePricePerNight() {
    let text;
    const numNights = this.numNights || 0;

    if (!this.totalPrice || numNights === 0) {
      text = "$--";
    } else {
      const pricePerGuestPerNight = this.totalPrice / numNights;
      text = _moneyFormat(pricePerGuestPerNight);
    }
    this.pricePerGuestTarget.textContent = `${text} / night`;
  }

  updatePricePerGuest() {
    const guestsCount =
      parseInt(this.adultsCountTarget.value, 10) +
      parseInt(this.childrenCountTarget.value, 10);
    let text;
    const options = {maximumFractionDigits: 2};

    if (!this.totalPrice || guestsCount === 0) {
      text = "$--";
    } else {
      const pricePerGuestPerNight = this.totalPrice / guestsCount;
      text = _moneyFormat(pricePerGuestPerNight);
    }
    this.pricePerGuestTarget.textContent = text;
  }

  updateTotalPrice(total) {
    this.totalPrice = parseFloat(total);
    this.totalPriceTarget.textContent = _moneyFormat(this.totalPrice);
    this.bottomBarTitleTarget.textContent = `Total before taxes: ${_moneyFormat(this.totalPrice)}`;
    this.updateSubmitButtonState();
    this.updateCartButtonState();
  }

  updateQuote(response) {
    // since the response didn't return sooner, assume success
    /* eslint-disable-next-line no-undef */
    Site.quoteStored = true;

    const {
      guest_fees: feeItems,
      nightly_rate: nightlyRate,
      num_nights: numNights,
      total_rate: totalRate,
      total_taxes: taxes,
      rate_name: rateName,
      rate_type_id: rateTypeId,
      additional_rate_types: rateTypes,
      total
    } = response;


    const totalWithoutTaxes = total - taxes;
    this.numNights = numNights;
    const totalDiscount = parseFloat(response.total_discount) || 0;
    const promoApplied = response.promo_code && totalDiscount > 0;
    const displayedTotalRate = totalRate - totalDiscount;
    const displayedNightly = numNights > 0 ? displayedTotalRate / numNights : nightlyRate;
    const isStandardRate = (rateName || "").toLowerCase().includes("standard");
    this.updateRateTypes(rateTypes, rateTypeId, promoApplied);
    this.updateFees(feeItems);
    this.updateNightlyAndTotalRate(displayedNightly, numNights, displayedTotalRate);
    this.updateCouponDiscount(response);
    this.updatePromoRateHeader(response, promoApplied);
    this.updateTotalPrice(totalWithoutTaxes);
    //this.updatePricePerNight();
    //this.updatePricePerGuest();
    this.updatePricePerRoom();
    this.showRateAndSetName(promoApplied && isStandardRate ? "Promo Rate" : rateName);
    this.updateSubmitButtonState();
  }

  updatePromoRateHeader(response, promoApplied) {
    if (!this.hasPromoRateHeaderTarget) return;
    const numNights = parseInt(response.num_nights, 10) || 0;
    const nightlyRate = parseFloat(response.nightly_rate) || 0;
    const totalDiscount = parseFloat(response.total_discount) || 0;
    const showHeader = promoApplied && numNights > 0 && totalDiscount > 0;

    if (showHeader) {
      const promoNightly = nightlyRate - (totalDiscount / numNights);
      if (this.hasPromoRateOldPriceTarget) {
        this.promoRateOldPriceTarget.textContent = _moneyFormat(nightlyRate);
      }
      if (this.hasPromoRateNewPriceTarget) {
        this.promoRateNewPriceTarget.textContent = _moneyFormat(promoNightly);
      }
      this.promoRateHeaderTarget.style.display = "";
      if (this.hasPriceRateTypesTarget) this.priceRateTypesTarget.style.display = "none";
    } else {
      this.promoRateHeaderTarget.style.display = "none";
      if (this.hasPriceRateTypesTarget) this.priceRateTypesTarget.style.display = "";
    }
  }

  updateCouponDiscount(response) {
    const code = response.promo_code;
    const value = parseFloat(response.total_discount);
    const applied = code && value > 0;

    if (this.hasCouponDiscountTarget) {
      // The nightly line already shows the post-promo rate, so the receipt
      // does not need a separate discount line. The savings banner below
      // still surfaces the dollar amount saved.
      this.couponDiscountTarget.style.display = "none";
    }

    if (this.hasCouponSavingsBannerTarget) {
      if (applied) {
        this.couponSavingsBannerTarget.style.display = "";
        if (this.hasCouponSavingsCodeTarget) this.couponSavingsCodeTarget.textContent = code;
        if (this.hasCouponSavingsValueTarget) {
          this.couponSavingsValueTarget.textContent = _moneyFormat(value);
        }
      } else {
        this.couponSavingsBannerTarget.style.display = "none";
      }
    }
  }

  handleAddToCart() {
    newCart.add_item(this.bookingItemParams())
      .then(() => {
        this.pushCartDataLayer('add_to_cart');
        this.updateCartButtonState();
        window.dispatchEvent(new CustomEvent("cartUpdated"));
      })
      .catch(error => console.log(error));

  }

  handleUpdateInCart() {
    newCart.add_item(this.bookingItemParams())
      .then(() => {
        this.updateCartButtonState();
        window.dispatchEvent(new CustomEvent("cartUpdated"));
      })
      .catch(error => console.log(error));
  }

  handleClickTooltip() {
    const isVisible = this.tooltipTextTarget.style.visibility === "visible";
    this.tooltipTextTarget.style.visibility = isVisible ? "hidden" : "visible";
  }

  handleUnitType(unitType) {
    if (unitType !== "unit_type") return false;

    const modalId = "pick-own-villa-modal";
    const modal = document.getElementById(modalId);

    if (modal && !modal.classList.contains("is-open")) {
      MicroModal.show(modalId);
      return true;
    }

    return false;
  }

  handleDisabledButton() {
    const isDisabled = this.bookingBtnTarget.classList.contains(
      "disabled-button"
    );

    if (!isDisabled) return false;

    if (!this.isValidDates()) {
      this.arriveDateLabelTarget.click();
      return true;
    }

    if (!this.isValidDGuests()) {
      this.guestsCountMobileTarget.click();
      this.guestsWrapperTarget.classList.add("restricted-guests");
      return true;
    }
  }

  handleProceedToBooking(e) {
    e.preventDefault();

    book_now(this.bookingItemParams())
                 .then(cart => {
      this.pushCartDataLayer('add_to_cart');
      cart.checkout()
    })
  }

  pushDataLayer(quote) {
    if (!window.unitLayer) return;
    let data = {...window.unitLayer};
    // nightly_rate is the pre-discount rate. Report the discounted nightly rate
    // (gross room rate minus the actual discount, per night) so price_per_night
    // reflects what the guest actually pays rather than the full price.
    const numNights = quote.num_nights;
    const totalDiscount = parseFloat(quote.total_discount) || 0;
    const discountedRoomRate = (parseFloat(quote.total_rate) || 0) - totalDiscount;
    const displayedNightly = numNights > 0
      ? discountedRoomRate / numNights
      : quote.nightly_rate;
    data.cartTotal = quote.total;
    data.ecommerce.total_quantity = numNights;
    data.ecommerce.price_per_night = Number(displayedNightly.toFixed(2));

    const totalGuests = this.adultsCountTarget.value + this.childrenCountTarget.value
    data.ecommerce.number_of_guests = totalGuests;

    window.unitLayer = data;
    window.dataLayer.push(data);

    this.pushCartDataLayer('view_item');
    // let dataGTM = { ...window.unitLayerGTM }
    // dataGTM.ecommerce.items[0].price = quote.nightly_rate;
    // window.dataLayer.push(dataGTM);
  }

  pushCartDataLayer(event_name) {
    const eventData = {...window.unitLayerGTM}
    const code = window.unitLayer.ecommerce.house_sku;
    eventData.event_name = event_name;

    // nightly_rate/total_rate are the pre-discount room rates. Report the
    // discounted rates (gross room rate minus the actual discount) so analytics
    // matches the price the guest actually pays (total is already net of it).
    const numNights = this.currentQuote.num_nights
    const totalDiscount = parseFloat(this.currentQuote.total_discount) || 0
    const displayedTotalRate = (parseFloat(this.currentQuote.total_rate) || 0) - totalDiscount
    const displayedNightly = numNights > 0
      ? Number((displayedTotalRate / numNights).toFixed(2))
      : this.currentQuote.nightly_rate

    eventData.ecommerce.value = this.currentQuote.total
    eventData.ecommerce.price_base = displayedTotalRate
    eventData.ecommerce.price_tax_fees = Number((this.currentQuote.total_fees + this.currentQuote.total_taxes).toFixed(2))
    const unit = eventData.ecommerce.items[0]
    unit.rate_plan_name = this.currentQuote.rate_name
    unit.checkin_date = this.currentQuote.arrival_date
    unit.checkout_date = this.currentQuote.departure_date
    unit.total_guests = Number(this.adultsCountTarget.value) + Number(this.childrenCountTarget.value)
    unit.adults = Number(this.adultsCountTarget.value)
    unit.children = Number(this.childrenCountTarget.value)
    unit.price = displayedTotalRate
    unit.quantity = 1
    unit.price_per_night = displayedNightly
    unit.number_of_nights = numNights

    eventData.ecommerce.items[0] = unit
    this.currentQuote.guest_fees.forEach(fee => {
      eventData.ecommerce.items.push({
        item_id: `${code}-${fee.name}`,
        item_name: fee.name,
        item_brand: 'Villatel',
        item_category: 'Fee',
        price: fee.unit_value,
        quantity: fee.quantity
      })
    })
    window.unitLayerGTM = eventData;
    window.dataLayer.push(eventData);
  }
}
